import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserCompetency, WorkerSiteCompetencyCounts, WorkOrderCompetency, WorkOrderCompetencyGroup } from 'app/_models/competency.model';
import { SiteOwnerAuthorisation } from 'app/_models/site-owner-authorisation.model';
import { ReferenceTurbine } from 'app/_models/turbine.model';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';

export interface CompetencyTestFailure {
  requiredCompetencyId: number;
  requiredCompetencyName: string;
  requiredCompetencyShortName: string;
  requiredCompetencyTypeId: number;
  manufacturer: string;
  model: string;
  rating: string;
  failureReason: string;
}

export interface WorkOrderCompetencyCheckResult {
  workOrderId: number;
  skylarkUserId: number;
  testPassed: boolean;
  competencyTestFailures: CompetencyTestFailure[];
  requiredCompetencies: WorkOrderCompetency[];
}

@Injectable()
export class CompetencyService extends BaseHttpClientService {
  constructor(private http: HttpClient) {
    super();
  }

  public loadUserCompetencyStatus(selectedWorkerId: number, siteOwnerId: number): Observable<UserCompetency[]> {
    // siteOwnerId is required here. UserCompetency currently flattens a single approval from
    // competencyApprovals[0], so calling this endpoint without a siteOwnerId would be incorrect
    // for competencies that have approvals from multiple site owners. If this method is changed
    // to support the unfiltered endpoint response, update the model/mapping first.
    return this.http
      .get<any>(`${environment.competencyApiUrl}api/user-competency-status/${selectedWorkerId}?siteOwnerId=${siteOwnerId}`)
      .pipe(map(function (response) {
        return (response.competencyStatuses || []).map(function (competency) {
          return new UserCompetency().deserialize(competency);
        });
      }))
      .pipe(catchError(this.handleError));
  }

  public getMissingSiteCompetenciesForWorker(
    siteId: number,
    workerSkylarkUserId: number,
  ): Observable<Array<{ competencyId: number, competencyName: string, shortName: string }>> {
    return this.http
      .get<any[]>(`${environment.competencyApiUrl}api/site-competency/check/${siteId}/${workerSkylarkUserId}`)
      .pipe(map((items) => items || []))
      .pipe(catchError(this.handleError));
  }

  public approveCompetency(competencyId: number, siteOwnerId: number, expiryDate: string, file?: File): Observable<any> {
    const statusUpdate = {
      siteOwnerSkylarkUserId: siteOwnerId,
      status: 'APPROVED',
      expiryDate: expiryDate,
    };
    const formData = new FormData();
    formData.append('statusUpdate', new Blob([JSON.stringify(statusUpdate)], { type: 'application/json' }));
    if (file) {
      formData.append('file', file, file.name);
    }
    return this.http
      .put<any>(`${environment.competencyApiUrl}api/worker-competency/${competencyId}/approval`, formData)
      .pipe(catchError(this.handleError));
  }

  public rejectCompetency(competencyId: number, siteOwnerId: number, reason: string): Observable<any> {
    const statusUpdate = {
      siteOwnerSkylarkUserId: siteOwnerId,
      status: 'DOCUMENT_REJECTED',
      reason: reason,
    };
    const formData = new FormData();
    formData.append('statusUpdate', new Blob([JSON.stringify(statusUpdate)], { type: 'application/json' }));
    return this.http
      .put<any>(`${environment.competencyApiUrl}api/worker-competency/${competencyId}/approval`, formData)
      .pipe(catchError(this.handleError));
  }

  public deleteCompetency(competencyId: number, siteOwnerId: number): Observable<any> {
    const params = new HttpParams().set('siteOwnerSkylarkUserId', String(siteOwnerId));
    return this.http
      .delete<any>(`${environment.competencyApiUrl}api/worker-competency/${competencyId}/approval`, { params })
      .pipe(catchError(this.handleError));
  }

  // Real Endpoints

  public getCompetencyCountsForSite(
    skylarkUserIds: number[],
    siteId: number,
    siteOwnerId: number,
  ): Observable<WorkerSiteCompetencyCounts[]> {
    if (!Array.isArray(skylarkUserIds) || !skylarkUserIds.length || !siteId || !siteOwnerId) {
      return of([]);
    }

    const uniqueIds = Array.from(new Set(skylarkUserIds.filter(id => !!id)));
    const body = { skylarkUserIds: uniqueIds, siteId, siteOwnerId };

    return this.http
      .post<WorkerSiteCompetencyCounts[]>(`${environment.competencyApiUrl}api/user-competency-status/counts`, body)
      .pipe(catchError(this.handleError));
  }

  public searchUserCompetencyStatus(
    skylarkUserIds: number[],
    siteOwnerId: number
  ): Observable<Array<{ skylarkUserId: number, competencies: UserCompetency[] }>> {
    if (!Array.isArray(skylarkUserIds) || !skylarkUserIds.length || !siteOwnerId) {
      return of([]);
    }

    const uniqueIds = Array.from(new Set(skylarkUserIds.filter(function (id) { return !!id; })));
    const body = {
      skylarkUserIds: uniqueIds,
      siteOwnerId: siteOwnerId,
    };

    return this.http
      .post<any[]>(environment.competencyApiUrl + 'api/user-competency-status/search', body)
      .pipe(map(function (response) {
        return (response || []).map(function (item) {
          return {
            skylarkUserId: item.skylarkUserId,
            competencies: (item.competencyStatuses || []).map(function (comp) {
              return new UserCompetency().deserialize(comp);
            }),
          };
        });
      }))
      .pipe(catchError(function (error) {
        console.error('[competency] user-competency-status/search error', {
          status: error && error.status,
          message: error && error.message,
          body: body,
        });
        return throwError(error);
      }));
  }

  public getWorkOrderCompetencies(workOrderId: number): Observable<WorkOrderCompetency[]> {
    return this.http
      .get<any[]>(`${environment.competencyApiUrl}api/work-order-required-competency/by-work-order/${workOrderId}`)
      .pipe(map((competencies) => competencies.map((competency) => new WorkOrderCompetency().deserialize(competency))))
      .pipe(catchError(this.handleError));
  }

  public getWorkOrderCompetenciesBulk(workOrderIds: number[]): Observable<WorkOrderCompetencyGroup[]> {
    const headers = this.applyLatestOnlyHeader();

    return this.http
      .post<any[]>(environment.competencyApiUrl + 'api/work-order-required-competency/multiple-work-orders', { workOrderIds }, { headers })
      .pipe(map((items) => items.map((item) => new WorkOrderCompetencyGroup().deserialize(item))))
      .pipe(catchError(this.handleError));
  }

  public deleteWorkOrderCompetency(competencyId: number): Observable<any> {
    return this.http
      .delete<any>(`${environment.competencyApiUrl}api/work-order-required-competency/${competencyId}`)
      .pipe(catchError(this.handleError));
  }

  //Turbine calls for drop down
  public getTurbineManufacturers(): Observable<string[]> {
    return this.http
      .get<string[]>(`${environment.competencyApiUrl}api/turbine/manufacturer`)
      .pipe(catchError(this.handleError));
  }

  public searchTurbines(filter: { manufacturer?: string, name?: string, model?: string }): Observable<ReferenceTurbine[]> {
    return this.http
      .post<any[]>(`${environment.competencyApiUrl}api/turbine/search`, filter)
      .pipe(map(items => items.map(item => new ReferenceTurbine().deserialize(item))))
      .pipe(catchError(this.handleError));
  }

  //POST call to add competency — multipart/form-data (JSON metadata + optional file)
  public addCompetencyToUser(payload: {
    skylarkUserId: number,
    competency_type: string,
    manufacturer: string,
    models: string[],
    ratings: string[],
    notes: string,
    expiry_date: string,
    request_evidence?: boolean,
  }, file?: File): Observable<any> {
    const formData = new FormData();
    formData.append('competency', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    if (file) {
      formData.append('file', file, file.name);
    }
    return this.http
      .post<any>(`${environment.competencyApiUrl}api/worker-competency`, formData)
      .pipe(catchError((error) => this.handleCompetencySubmitError(error)));
  }


  //Authorisation notes
  public getAuthorisationNotes(skylarkUserId: number, siteOwnerId: number): Observable<SiteOwnerAuthorisation[]> {
    return this.http
      .get<any[]>(
        `${environment.competencyApiUrl}api/site-owner-authorisation/user-competency-notes/${skylarkUserId}/for-owner/${siteOwnerId}`
      )
      .pipe(map(items => items.map(item => new SiteOwnerAuthorisation().deserialize(item))))
      .pipe(catchError(this.handleError));
  }

  public requestEvidence(siteOwnerId: number, competencyId: number): Observable<any> {
    return this.http
      .post<any>(
        environment.competencyApiUrl + 'api/worker-competency/' + competencyId + '/request-evidence',
        { siteOwnerSkylarkUserId: siteOwnerId }
      )
      .pipe(catchError(this.handleError));
  }

  public uploadCompetencyEvidence(
    competencyId: number,
    siteOwnerId: number,
    file: File,
    expiryDate: string
  ): Observable<any> {

    const data = {
      siteOwnerSkylarkUserId: siteOwnerId,
      expiryDate: expiryDate,
    };

    const formData = new FormData();
    formData.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
    formData.append('file', file, file.name);

    return this.http
      .put<any>(
        `${environment.competencyApiUrl}api/worker-competency/${competencyId}/supporting-document`,
        formData
      )
      .pipe(catchError((error) => {
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('Infected');
        } else if (error.error && error.error.status === 400 && error.error.errorKey === 'EmbeddedScript') {
          throw new Error('EmbeddedScript');
        }
        return this.handleError(error);
      }));
  }

  public updateAuthorisationNotes(payload: {
    siteOwnerAuthorisationId: number,
    siteOwnerSkylarkUserId: number,
    skylarkUserId: number,
    competencyNotes: string,
    competencyApprovalStatus: string,
    expiryDate: string,
  }): Observable<any> {
    return this.http
      .put<any>(`${environment.competencyApiUrl}api/site-owner-authorisation/update-competency-notes`, payload)
      .pipe(catchError(this.handleError));
  }

  public createAuthorisationNotes(payload: {
    siteOwnerSkylarkUserId: number,
    skylarkUserId: number,
    competencyNotes: string,
    competencyApprovalStatus: string,
    expiryDate: string,
  }): Observable<SiteOwnerAuthorisation> {
    return this.http
      .post<any>(`${environment.competencyApiUrl}api/site-owner-authorisation/create-competency-notes`, payload)
      .pipe(map(item => new SiteOwnerAuthorisation().deserialize(item)))
      .pipe(catchError(this.handleError));
  }

  public getCompetencySubmitError(error: any, fallbackMessage: string): string {
    if (error instanceof Error) {
      if (error.message === 'Infected') {
        return 'File is infected with malware!';
      }
      if (error.message === 'EmbeddedScript') {
        return 'File has embedded script - please submit file to Skylark for analysis';
      }
    }

    return (error && error.error && error.error.detail) || fallbackMessage;
  }

  private handleCompetencySubmitError(error: any): Observable<never> {
    if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
      return throwError(new Error('Infected'));
    }

    if (error.error && error.error.status === 400 && error.error.errorKey === 'EmbeddedScript') {
      return throwError(new Error('EmbeddedScript'));
    }

    const backendMessage = error && error.error ? error.error.detail : '';
    if (backendMessage) {
      return throwError({ error: { detail: backendMessage } });
    }

    return this.handleError(error);
  }

  public checkWorkOrderCompetenciesForWorker(
    workOrderId: number,
    workerId: number,
    isAdditionalWorker: boolean,
  ): Observable<WorkOrderCompetencyCheckResult> {
    const params = new HttpParams()
      .set('isAdditionalWorker', String(isAdditionalWorker));

    return this.http
      .get<WorkOrderCompetencyCheckResult>(
        `${environment.competencyApiUrl}api/worker-competency/check-work-order/${workOrderId}/for-worker/${workerId}`,
        { params },
      )
      .pipe(catchError(this.handleError));
  }

}
